/**
 * PerspectivePanel — read-only view of the AI's structured perspective on a
 * relationship (Memory Plan v1, Phase 3).
 *
 * Future enhancements (Phase 5) will turn this read-only panel into an editor.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Loader2, AlertCircle, Lock, PencilLine, RefreshCw, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { toast } from "sonner";
import ClaimEditorRow from "./ClaimEditorRow";
import SeedFromNotes from "./SeedFromNotes";
import PerspectiveInfoDialog from "./PerspectiveInfoDialog";
import { authAxios } from "../../utils/auth";

const DOMAIN_LABELS = {
  current_overall_state: "Current overall state",
  communication: "Communication",
  trust: "Trust",
  financial_trust: "Financial trust",
  emotional_safety: "Emotional safety",
  intimacy: "Intimacy",
  conflict_handling: "Conflict handling",
  future_alignment: "Future alignment",
  growth_edges: "Growth edges",
  appreciation: "Appreciation",
  unmet_needs: "Unmet needs",
  external_pressures: "External pressures",
};

const CONFIDENCE_COLOR = {
  high: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  medium: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  low: "text-white/50 border-white/20 bg-white/5",
};

function ConfidenceBadge({ label, value }) {
  const cls = CONFIDENCE_COLOR[label] || CONFIDENCE_COLOR.low;
  return (
    <span
      data-testid={`perspective-confidence-${label}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${cls}`}
      title={`Confidence: ${(value * 100).toFixed(0)}%`}
    >
      {label}
    </span>
  );
}

function ClaimRow({ claim, matchId, onChange, isSelected, onToggleSelect }) {
  return (
    <li
      data-testid={`perspective-claim-${claim.claim_id}`}
      className={`border rounded-md px-3 py-2 transition-colors ${
        isSelected
          ? "border-emerald-500/40 bg-emerald-500/5"
          : "border-divine-gold/15 bg-divine-black/40"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-divine-gold/90 text-xs uppercase tracking-wide mb-0.5">
            {claim.topic || "—"}
          </div>
          <p className="text-white/90 text-sm leading-snug">{claim.claim_text}</p>
          {claim.evidence_summary ? (
            <p className="text-white/40 text-xs mt-1 italic">
              {claim.evidence_summary}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <ConfidenceBadge
            label={claim.confidence_label}
            value={claim.confidence}
          />
          {claim.user_locked ? (
            <Lock
              className="w-3 h-3 text-divine-gold/70"
              data-testid={`perspective-locked-${claim.claim_id}`}
            />
          ) : null}
          {claim.user_edited ? (
            <PencilLine
              className="w-3 h-3 text-emerald-400/80"
              data-testid={`perspective-edited-${claim.claim_id}`}
            />
          ) : null}
        </div>
      </div>
      {matchId ? (
        <ClaimEditorRow
          claim={claim}
          matchId={matchId}
          onChange={onChange}
          isSelected={isSelected}
          onToggleSelect={onToggleSelect}
        />
      ) : null}
    </li>
  );
}

function PendingQueueRow({ entry }) {
  return (
    <li
      data-testid={`perspective-pending-${entry.queue_id}`}
      className="border border-amber-500/30 rounded-md px-3 py-2 bg-amber-500/5"
    >
      <div className="text-amber-300 text-[10px] uppercase tracking-wide mb-0.5">
        Awaiting your confirmation · {entry.claim_domain}
      </div>
      <div className="text-white/85 text-sm leading-snug">{entry.claim_text}</div>
      <div className="text-white/40 text-xs mt-1">
        Reply yes / no in your next coaching message to confirm or correct.
      </div>
    </li>
  );
}

export default function PerspectivePanel({ matchId, refreshKey = 0, coachSessionId = null }) {
  const [perspective, setPerspective] = useState(null);
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  // Bulk-resolve state. Cleared on every load() / matchId change so a
  // panel refresh always starts with an empty selection.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);

  const toggleSelect = useCallback((claimId) => {
    // Auto-dismiss the hint modal the moment any selection is made —
    // matches choice 2c ("only show the modal if NO selection is made").
    setHintOpen(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(claimId)) next.delete(claimId);
      else next.add(claimId);
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    if (!matchId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // Clear bulk selection on every refresh — choice 5a.
    setSelectedIds(new Set());
    try {
      // Cache-busting timestamp to defeat any intermediate caching layer
      const bust = Date.now();
      // authAxios handles bearer-token injection, auto-refresh on 401, and one-shot retry
      const [pRes, qRes] = await Promise.allSettled([
        authAxios.get(`/relationship/${matchId}/perspective`, {
          params: { _: bust },
          headers: { "Cache-Control": "no-store" },
        }),
        authAxios.get(`/relationship/${matchId}/perspective/queue`, {
          params: { _: bust },
          headers: { "Cache-Control": "no-store" },
        }),
      ]);
      if (pRes.status === "rejected") {
        const err = pRes.reason;
        const status = err?.response?.status;
        throw new Error(`Perspective load failed${status ? ` (${status})` : ""}`);
      }
      setPerspective(pRes.value.data);
      if (qRes.status === "fulfilled") {
        setQueue(qRes.value.data);
      } else {
        setQueue(null);
      }
      setLastLoadedAt(new Date());
    } catch (e) {
      setError(e.message || "Failed to load perspective");
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  // Re-fetch whenever matchId OR refreshKey changes.
  // refreshKey is bumped by the parent every time the Sheet opens.
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Bulk-resolve: PATCH each selected claim with `action: "resolve"`,
  // collect successes/failures, then refresh. The endpoint already
  // exists per-claim (used by the legacy single-tick flow) — we just
  // fan it out so the user can clear a batch in one click.
  const runBulkResolve = useCallback(async () => {
    if (selectedIds.size === 0) {
      // No selection — show the hint modal (choice 2c). Modal auto
      // disappears when the user makes a selection.
      setHintOpen(true);
      return;
    }
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(
      ids.map((claimId) =>
        authAxios.patch(`/relationship/${matchId}/perspective/${claimId}`, {
          action: "resolve",
          payload: { reason: "Bulk-resolved via Claims panel" },
        })
      )
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    const succeeded = ids.length - failed;
    if (succeeded > 0) {
      toast.success(
        `Resolved ${succeeded} claim${succeeded === 1 ? "" : "s"}` +
          (failed ? ` · ${failed} failed` : "")
      );
    } else {
      toast.error(`Failed to resolve ${failed} claim${failed === 1 ? "" : "s"}`);
    }
    setBulkBusy(false);
    // load() also clears selectedIds so we don't need to wipe it here.
    await load();
  }, [selectedIds, matchId, load]);

  if (!matchId) {
    return (
      <div
        data-testid="perspective-empty-no-match"
        className="text-white/40 text-sm p-4"
      >
        Select a match to see your structured perspective.
      </div>
    );
  }

  if (loading) {
    return (
      <div
        data-testid="perspective-loading"
        className="flex items-center justify-center p-6 text-white/60"
      >
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        Loading perspective…
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="perspective-error"
        className="flex items-start gap-2 p-4 text-rose-300 text-sm"
      >
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  const domains = perspective?.domains || {};
  const domainKeys = Object.keys(domains);
  const claimCount = perspective?.claim_count || 0;
  const pendingCount = queue?.pending_count || 0;

  return (
    <div data-testid="perspective-panel" className="space-y-4">
      <header className="space-y-1 relative">
        <PerspectiveInfoDialog />
        <div className="flex items-start justify-between gap-2 pr-10">
          <h3 className="font-cinzel text-divine-gold text-lg">
            Perspective on this relationship
          </h3>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="shrink-0 text-white/50 hover:text-divine-gold transition-colors disabled:opacity-40 disabled:cursor-not-allowed p-1 -mr-1"
            title="Refresh perspective"
            data-testid="perspective-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
        <p className="text-white/40 text-xs leading-relaxed">
          {claimCount} active claim{claimCount === 1 ? "" : "s"}
          {pendingCount ? ` · ${pendingCount} awaiting confirmation` : ""}
          . This is what Eros currently believes, grouped by domain.
        </p>
        {/* Bulk RESOLVE button — dark when no selection, glowing emerald
            when ≥1 claim is ticked. Click with 0 selected → hint modal. */}
        {claimCount > 0 ? (
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={runBulkResolve}
              disabled={bulkBusy}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider border-2 transition-all duration-200 ${
                selectedIds.size > 0
                  ? "bg-emerald-500/20 border-emerald-400 text-emerald-200 shadow-[0_0_12px_rgba(16,185,129,0.4)] hover:bg-emerald-500/30"
                  : "bg-divine-black/60 border-divine-gold/60 text-divine-gold/80 hover:text-divine-gold hover:border-divine-gold hover:bg-divine-gold/5"
              } disabled:opacity-40 disabled:cursor-wait`}
              data-testid="perspective-bulk-resolve-btn"
              title={
                selectedIds.size > 0
                  ? `Resolve ${selectedIds.size} selected claim${selectedIds.size === 1 ? "" : "s"}`
                  : "Resolve selected claims"
              }
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {bulkBusy
                ? "Resolving…"
                : selectedIds.size > 0
                ? `Resolve (${selectedIds.size})`
                : "Resolve"}
            </button>
          </div>
        ) : null}
        {lastLoadedAt ? (
          <p
            className="text-white/30 text-[10px]"
            data-testid="perspective-last-loaded"
          >
            Last refreshed {lastLoadedAt.toLocaleTimeString()}
          </p>
        ) : null}
      </header>

      {pendingCount > 0 ? (
        <section data-testid="perspective-pending-section" className="space-y-2">
          <h4 className="text-amber-300 text-xs uppercase tracking-wide">
            Awaiting confirmation
          </h4>
          <ul className="space-y-2">
            {queue.queue.map((q) => (
              <PendingQueueRow key={q.queue_id} entry={q} />
            ))}
          </ul>
        </section>
      ) : null}

      {claimCount === 0 ? (
        <div
          data-testid="perspective-empty-no-claims"
          className="text-white/40 text-sm border border-white/10 rounded-md p-4"
        >
          No structured claims yet. As you have coaching sessions about this
          relationship, Eros will populate this view.
        </div>
      ) : (
        <div className="space-y-4">
          {domainKeys.map((domain) => (
            <section
              key={domain}
              data-testid={`perspective-domain-${domain}`}
              className="space-y-2"
            >
              <h4 className="text-divine-gold/80 text-xs uppercase tracking-wide">
                {DOMAIN_LABELS[domain] || domain}
              </h4>
              <ul className="space-y-2">
                {domains[domain].map((c) => (
                  <ClaimRow
                    key={c.claim_id}
                    claim={c}
                    matchId={matchId}
                    onChange={load}
                    isSelected={selectedIds.has(c.claim_id)}
                    onToggleSelect={toggleSelect}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {matchId ? (
        <SeedFromNotes
          matchId={matchId}
          coachSessionId={coachSessionId}
          onCommit={load}
        />
      ) : null}

      {/* Hint modal — fires only when user clicks the dark RESOLVE
          button with zero selections. Closes immediately on backdrop or
          when a tick is made. */}
      <Dialog open={hintOpen} onOpenChange={setHintOpen}>
        <DialogContent
          className="bg-divine-charcoal border-divine-gold/25 text-white/85 max-w-sm"
          data-testid="perspective-bulk-resolve-hint"
        >
          <DialogHeader>
            <DialogTitle className="font-cinzel text-divine-gold flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Select claims to resolve
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-relaxed text-white/75">
            Select the claims that have been resolved to move them off
            the list. Tick the circle next to each claim, then click
            <span className="text-emerald-300 font-semibold"> Resolve </span>
            at the top.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
